import React from "react";
import {
  ArrayField,
  ChipField,
  Datagrid,
  DatagridConfigurable,
  DateField,
  FunctionField,
  List,
  NumberField,
  ReferenceArrayField,
  ReferenceField,
  SelectField,
  SelectInput,
  Show,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  useShowController,
} from "react-admin";
import Box from "@mui/material/Box";

import { createAdjustUnitPriceChoices, createPublishStateChoices } from "../components/choices";
import { StoragePeriodsField } from "../components/storage_periods_field";
import { ExistsSpecialConditionsField } from "../components/exists_special_conditions_field";
import { PrefecturesSelectField } from "../components/prefectures_select_field";
import { ShowToolbar } from "../components/ShowToolbar";
import { ListActions } from "../components/list_actions";
import { commonStyles } from "../components/common_styles";
import {
  formatDisplayOfferUnitPrice,
  formatDisplayStoragePeriodBonus,
  priceCalculationModeChoices,
} from "../utils/price_calculation";

const OthersOfferFilter = [
  <TextInput key="annum_name" source="annum_name" />,
  <TextInput key="prefecture_name" source="prefecture_name" />,
  <TextInput key="brand_name" source="brand_name" />,
  <TextInput key="grade_name" source="grade_name" />,
  <SelectInput key="can_adjust_unit_price" source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} />,
  <SelectInput key="publish_status" source="publish_status" choices={createPublishStateChoices()} />,
  <TextInput key="offer_publication_period_name" source="offer_publication_period_name" />,
];

export const OthersOfferList = (props: any) => {
  const preferenceKey = "others_offers";

  return (
    <List
      {...props}
      actions={<ListActions filters={OthersOfferFilter} preferenceKey={preferenceKey} />}
      filters={OthersOfferFilter}
      exporter={false}
      sort={{ field: "offer_publication_period_id", order: "DESC" }}
    >
      <DatagridConfigurable bulkActionButtons={false} preferenceKey={preferenceKey} rowClick="show">
        <ReferenceField source="annum_id" reference="annums" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <PrefecturesSelectField source="prefecture_ids" reference="prefectures" />
        <ReferenceField source="region_id" reference="regions" link={false} sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="brand_id" reference="brands" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="grade_id" reference="grades" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} sortable={false} />
        <FunctionField source="unit_price" render={formatDisplayOfferUnitPrice} sortable={false} />
        <SelectField source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} sortable={false} />
        <NumberField source="total_trading_units" sortable={false} />
        <StoragePeriodsField source="storage_period_ids" sortable={false} />
        <ExistsSpecialConditionsField source="special_condition_ids" sortable={false} />
        <SelectField source="publish_status" choices={createPublishStateChoices()} sortable={false} />
        <ReferenceField source="offer_publication_period_id" reference="offer_publication_periods" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <DateField source="publish_start_date" locales={"ja"} sortable={false} />
        <DateField source="publish_end_date" locales={"ja"} sortable={false} />
      </DatagridConfigurable>
    </List>
  );
};

export const OthersOfferShow = (props: any) => {
  const { record } = useShowController();

  if (!record) {
    return null;
  }

  return (
    <Show {...props} title="resources.others_offers.name" actions={<ShowToolbar listPath="/others_offers" />}>
      <SimpleShowLayout>
        <ReferenceField source="annum_id" reference="annums" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="prefecture_ids" reference="prefectures">
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <ReferenceField source="region_id" reference="regions" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="brand_id" reference="brands" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="grade_id" reference="grades" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="packaging_form_ids" reference="packaging_forms">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false}>
              <TextField
                source="name"
                sortable={false}
                label="resources.others_offers.fields.packaging_form_ids.name"
              />
              <NumberField
                source="deal_amount"
                sortable={false}
                label="resources.others_offers.fields.packaging_form_ids.deal_amount"
              />
            </Datagrid>
          </Box>
        </ReferenceArrayField>
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
        <FunctionField source="unit_price" render={formatDisplayOfferUnitPrice} />
        <SelectField source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} />
        <ArrayField source="grade_penalties">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <ReferenceField source="grade_id" reference="grades" link={false}>
                <TextField source="name" />
              </ReferenceField>
              <NumberField source="penalty_in_yen" emptyText={"受け取らない"} />
            </Datagrid>
          </Box>
        </ArrayField>
        <NumberField source="total_trading_units" />
        <NumberField source="lowest_trading_units" />
        <NumberField source="highest_trading_units" />
        <ReferenceArrayField source="storage_period_ids" reference="storage_periods" linkType={false}>
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false}>
              <TextField label="resources.storage_periods.fields.name" source="name" />
              <NumberField
                label="resources.storage_periods.fields.storage_period_in_days"
                source="storage_period_in_days"
              />
              <FunctionField
                label="resources.storage_periods.fields.bonus_in_yen"
                render={(storagePeriodRecord: any) =>
                  formatDisplayStoragePeriodBonus(storagePeriodRecord, record.price_calculation_mode)
                }
              />
            </Datagrid>
          </Box>
        </ReferenceArrayField>
        <ReferenceArrayField
          source="special_condition_ids"
          reference="special_conditions"
          className="array-field-that-potential-empty"
        >
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <SelectField source="publish_status" choices={createPublishStateChoices()} />
        <ReferenceField source="offer_publication_period_id" reference="offer_publication_periods" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <DateField source="publish_start_date" locales={"ja"} />
        <DateField source="publish_end_date" locales={"ja"} />
      </SimpleShowLayout>
    </Show>
  );
};
